import { useState } from "react";
import { trpc } from "@/lib/trpc";
import { toast } from "sonner";

const LOGO_URL = "/manus-storage/forge-horizontal-v4_5568d021.png";

const FEATURES = [
  {
    icon: "⌚",
    tag: "LIVE NOW",
    title: "Apple Watch Import",
    description:
      "Photograph your Watch summary screen. Our AI extracts calories, heart rate, duration, and distance — no manual entry, no sync cables.",
    accent: "#4a7c59",
  },
  {
    icon: "📈",
    tag: "LIVE NOW",
    title: "Plateau Detection",
    description:
      "Forge tracks your progressive overload week over week. When your numbers stall, it flags the lift so you can adjust before you waste another training block.",
    accent: "#4a7c59",
  },
  {
    icon: "🤖",
    tag: "COMING SOON",
    title: "AI Workout Generation",
    description:
      "Describe your goals, available equipment, and training history. Forge builds a periodized program tailored to your numbers — not a generic template.",
    accent: "#6b7280",
  },
];

const STATS = [
  { value: "100%", label: "Data Ownership" },
  { value: "∞", label: "Exercise Library" },
  { value: "0", label: "Subscription Tiers" },
];

export default function BetaLanding() {
  const [form, setForm] = useState({ name: "", email: "", gymName: "" });
  const [submitted, setSubmitted] = useState(false);

  const signupMutation = trpc.beta.signup.useMutation({
    onSuccess: () => {
      setSubmitted(true);
    },
    onError: (err) => {
      if (err.message.includes("already on the beta list")) {
        toast.error("That email is already registered for beta access.");
      } else {
        toast.error("Something went wrong. Try again in a moment.");
      }
    },
  });

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim()) return;
    signupMutation.mutate({
      name: form.name.trim(),
      email: form.email.trim(),
      gymName: form.gymName.trim() || undefined,
    });
  }

  return (
    <div
      className="min-h-screen text-white"
      style={{
        background: "linear-gradient(160deg, #0d0d0d 0%, #111a13 50%, #0d0d0d 100%)",
        fontFamily: "'Inter', 'system-ui', sans-serif",
      }}
    >
      {/* Noise texture overlay */}
      <div
        className="fixed inset-0 pointer-events-none opacity-[0.03]"
        style={{
          backgroundImage: `url("data:image/svg+xml,%3Csvg viewBox='0 0 256 256' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noise'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noise)'/%3E%3C/svg%3E")`,
          backgroundRepeat: "repeat",
          backgroundSize: "128px",
        }}
      />

      {/* Top rule line */}
      <div className="w-full h-px" style={{ background: "linear-gradient(90deg, transparent, #4a7c59, transparent)" }} />

      {/* ─── HERO ─────────────────────────────────────────────────────── */}
      <section className="relative overflow-hidden">
        {/* Background grid */}
        <div
          className="absolute inset-0 opacity-[0.04]"
          style={{
            backgroundImage:
              "linear-gradient(#4a7c59 1px, transparent 1px), linear-gradient(90deg, #4a7c59 1px, transparent 1px)",
            backgroundSize: "60px 60px",
          }}
        />

        <div className="relative max-w-5xl mx-auto px-6 pt-20 pb-16 text-center">
          {/* Beta badge */}
          <div className="inline-flex items-center gap-2 mb-8">
            <span
              className="px-3 py-1 text-xs font-bold tracking-[0.2em] uppercase border"
              style={{
                color: "#4a7c59",
                borderColor: "#4a7c59",
                background: "rgba(74,124,89,0.08)",
                letterSpacing: "0.2em",
              }}
            >
              ◆ CLOSED BETA
            </span>
          </div>

          {/* Logo */}
          <div className="flex justify-center mb-8">
            <img
              src={LOGO_URL}
              alt="Forge // Telemetry"
              className="w-full max-w-lg"
              style={{ filter: "brightness(1.05) contrast(1.02)" }}
            />
          </div>

          {/* Slogan */}
          <p
            className="text-lg md:text-xl font-light tracking-[0.15em] uppercase mb-6"
            style={{ color: "#a3b8a8", letterSpacing: "0.15em" }}
          >
            Track the Work.&nbsp;&nbsp;Trust the Data.&nbsp;&nbsp;Forge the Legacy.
          </p>

          {/* Sub-headline */}
          <p
            className="max-w-2xl mx-auto text-base md:text-lg leading-relaxed mb-12"
            style={{ color: "#6b7a6e" }}
          >
            A precision workout tracker built for athletes who take their training data
            seriously. No fluff, no gamification — just raw telemetry on every rep,
            every session, every PR.
          </p>

          {/* Stats row */}
          <div className="flex justify-center gap-12 mb-16">
            {STATS.map((s) => (
              <div key={s.label} className="text-center">
                <div
                  className="text-3xl font-bold mb-1"
                  style={{ color: "#4a7c59", fontVariantNumeric: "tabular-nums" }}
                >
                  {s.value}
                </div>
                <div className="text-xs tracking-widest uppercase" style={{ color: "#5a6b5e" }}>
                  {s.label}
                </div>
              </div>
            ))}
          </div>

          {/* CTA arrow */}
          <div className="flex flex-col items-center gap-2 animate-bounce">
            <span className="text-xs tracking-widest uppercase" style={{ color: "#4a7c59" }}>
              Apply Below
            </span>
            <svg width="16" height="24" viewBox="0 0 16 24" fill="none">
              <path d="M8 0v20M1 13l7 7 7-7" stroke="#4a7c59" strokeWidth="1.5" strokeLinecap="round" />
            </svg>
          </div>
        </div>
      </section>

      {/* ─── DIVIDER ─────────────────────────────────────────────────── */}
      <div className="max-w-5xl mx-auto px-6">
        <div className="flex items-center gap-4">
          <div className="flex-1 h-px" style={{ background: "rgba(74,124,89,0.3)" }} />
          <span className="text-xs tracking-widest uppercase" style={{ color: "#4a7c59" }}>
            FEATURE TELEMETRY
          </span>
          <div className="flex-1 h-px" style={{ background: "rgba(74,124,89,0.3)" }} />
        </div>
      </div>

      {/* ─── FEATURES ────────────────────────────────────────────────── */}
      <section className="max-w-5xl mx-auto px-6 py-16">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {FEATURES.map((f) => (
            <div
              key={f.title}
              className="relative p-6 border"
              style={{
                borderColor: f.accent === "#4a7c59" ? "rgba(74,124,89,0.35)" : "rgba(107,114,128,0.25)",
                background:
                  f.accent === "#4a7c59"
                    ? "linear-gradient(135deg, rgba(74,124,89,0.06) 0%, rgba(13,13,13,0) 100%)"
                    : "rgba(255,255,255,0.02)",
              }}
            >
              {/* Corner accent */}
              <div
                className="absolute top-0 left-0 w-6 h-6"
                style={{
                  borderTop: `2px solid ${f.accent}`,
                  borderLeft: `2px solid ${f.accent}`,
                  opacity: 0.7,
                }}
              />

              <div className="text-3xl mb-4">{f.icon}</div>

              <div className="flex items-center gap-2 mb-3">
                <span
                  className="text-xs font-bold tracking-widest uppercase px-2 py-0.5"
                  style={{
                    color: f.accent,
                    border: `1px solid ${f.accent}`,
                    background: `${f.accent}15`,
                  }}
                >
                  {f.tag}
                </span>
              </div>

              <h3
                className="text-lg font-bold mb-3 tracking-wide"
                style={{ color: "#e8ede9" }}
              >
                {f.title}
              </h3>

              <p className="text-sm leading-relaxed" style={{ color: "#6b7a6e" }}>
                {f.description}
              </p>
            </div>
          ))}
        </div>
      </section>

      {/* ─── DIVIDER ─────────────────────────────────────────────────── */}
      <div className="max-w-5xl mx-auto px-6">
        <div className="flex items-center gap-4">
          <div className="flex-1 h-px" style={{ background: "rgba(74,124,89,0.3)" }} />
          <span className="text-xs tracking-widest uppercase" style={{ color: "#4a7c59" }}>
            BETA ACCESS
          </span>
          <div className="flex-1 h-px" style={{ background: "rgba(74,124,89,0.3)" }} />
        </div>
      </div>

      {/* ─── SIGNUP FORM ─────────────────────────────────────────────── */}
      <section className="max-w-5xl mx-auto px-6 py-16">
        <div className="max-w-xl mx-auto">
          {submitted ? (
            /* Success state */
            <div
              className="text-center p-10 border"
              style={{ borderColor: "rgba(74,124,89,0.5)", background: "rgba(74,124,89,0.06)" }}
            >
              <div className="text-5xl mb-6">⚒️</div>
              <h2 className="text-2xl font-bold mb-3 tracking-wide" style={{ color: "#e8ede9" }}>
                Application Received
              </h2>
              <p className="text-sm leading-relaxed mb-6" style={{ color: "#6b7a6e" }}>
                You're on the list. Beta invites roll out in small batches — we'll reach out
                directly when your slot opens. In the meantime, keep forging.
              </p>
              <div
                className="text-xs tracking-widest uppercase"
                style={{ color: "#4a7c59" }}
              >
                ◆ FORGE // TELEMETRY
              </div>
            </div>
          ) : (
            /* Form */
            <div>
              <div className="mb-8">
                <h2
                  className="text-2xl md:text-3xl font-bold tracking-wide mb-3"
                  style={{ color: "#e8ede9" }}
                >
                  Apply for Beta Access
                </h2>
                <p className="text-sm leading-relaxed" style={{ color: "#6b7a6e" }}>
                  Beta access is limited and invite-only. Fill out the form below — we review
                  applications manually and reach out directly.
                </p>
              </div>

              <form onSubmit={handleSubmit} className="space-y-5">
                {/* Name */}
                <div>
                  <label
                    className="block text-xs font-bold tracking-widest uppercase mb-2"
                    style={{ color: "#4a7c59" }}
                    htmlFor="beta-name"
                  >
                    Full Name *
                  </label>
                  <input
                    id="beta-name"
                    type="text"
                    required
                    placeholder="John Smith"
                    value={form.name}
                    onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
                    className="w-full px-4 py-3 text-sm outline-none transition-colors"
                    style={{
                      background: "rgba(255,255,255,0.04)",
                      border: "1px solid rgba(74,124,89,0.3)",
                      color: "#e8ede9",
                    }}
                    onFocus={(e) => (e.currentTarget.style.borderColor = "rgba(74,124,89,0.8)")}
                    onBlur={(e) => (e.currentTarget.style.borderColor = "rgba(74,124,89,0.3)")}
                  />
                </div>

                {/* Email */}
                <div>
                  <label
                    className="block text-xs font-bold tracking-widest uppercase mb-2"
                    style={{ color: "#4a7c59" }}
                    htmlFor="beta-email"
                  >
                    Email Address *
                  </label>
                  <input
                    id="beta-email"
                    type="email"
                    required
                    placeholder="you@example.com"
                    value={form.email}
                    onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))}
                    className="w-full px-4 py-3 text-sm outline-none transition-colors"
                    style={{
                      background: "rgba(255,255,255,0.04)",
                      border: "1px solid rgba(74,124,89,0.3)",
                      color: "#e8ede9",
                    }}
                    onFocus={(e) => (e.currentTarget.style.borderColor = "rgba(74,124,89,0.8)")}
                    onBlur={(e) => (e.currentTarget.style.borderColor = "rgba(74,124,89,0.3)")}
                  />
                </div>

                {/* Gym */}
                <div>
                  <label
                    className="block text-xs font-bold tracking-widest uppercase mb-2"
                    style={{ color: "#4a7c59" }}
                    htmlFor="beta-gym"
                  >
                    Gym / Training Facility{" "}
                    <span style={{ color: "#4a5a4e", fontWeight: 400 }}>(optional)</span>
                  </label>
                  <input
                    id="beta-gym"
                    type="text"
                    placeholder="Iron Temple Gym, Dallas TX"
                    value={form.gymName}
                    onChange={(e) => setForm((f) => ({ ...f, gymName: e.target.value }))}
                    className="w-full px-4 py-3 text-sm outline-none transition-colors"
                    style={{
                      background: "rgba(255,255,255,0.04)",
                      border: "1px solid rgba(74,124,89,0.3)",
                      color: "#e8ede9",
                    }}
                    onFocus={(e) => (e.currentTarget.style.borderColor = "rgba(74,124,89,0.8)")}
                    onBlur={(e) => (e.currentTarget.style.borderColor = "rgba(74,124,89,0.3)")}
                  />
                </div>

                {/* Submit */}
                <button
                  type="submit"
                  disabled={signupMutation.isPending || !form.name.trim() || !form.email.trim()}
                  className="w-full py-4 text-sm font-bold tracking-[0.2em] uppercase transition-all duration-150"
                  style={{
                    background: signupMutation.isPending ? "rgba(74,124,89,0.4)" : "#4a7c59",
                    color: signupMutation.isPending ? "rgba(232,237,233,0.5)" : "#0d0d0d",
                    cursor: signupMutation.isPending ? "not-allowed" : "pointer",
                    letterSpacing: "0.2em",
                  }}
                  onMouseEnter={(e) => {
                    if (!signupMutation.isPending) {
                      e.currentTarget.style.background = "#5a9c6e";
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (!signupMutation.isPending) {
                      e.currentTarget.style.background = "#4a7c59";
                    }
                  }}
                  onMouseDown={(e) => {
                    e.currentTarget.style.transform = "scale(0.98)";
                  }}
                  onMouseUp={(e) => {
                    e.currentTarget.style.transform = "scale(1)";
                  }}
                >
                  {signupMutation.isPending ? "Submitting..." : "⚒ Apply for Beta Access"}
                </button>

                <p className="text-xs text-center" style={{ color: "#3a4a3e" }}>
                  No spam. No newsletters. Just a direct invite when your slot opens.
                </p>
              </form>
            </div>
          )}
        </div>
      </section>

      {/* ─── FOOTER ──────────────────────────────────────────────────── */}
      <footer className="border-t" style={{ borderColor: "rgba(74,124,89,0.15)" }}>
        <div className="max-w-5xl mx-auto px-6 py-10 flex flex-col md:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <img
              src={LOGO_URL}
              alt="Forge // Telemetry"
              className="h-6 opacity-60"
            />
          </div>
          <p className="text-xs tracking-widest uppercase" style={{ color: "#3a4a3e" }}>
            Track the Work. Trust the Data. Forge the Legacy.
          </p>
          <p className="text-xs" style={{ color: "#3a4a3e" }}>
            © {new Date().getFullYear()} Forge // Telemetry
          </p>
        </div>
      </footer>
    </div>
  );
}
